/**
 * FEEDBACK (C1) — as chamadas das telas: a aba "Feedback" da ficha, o "Painel de feedback", "Meu desenvolvimento" e o
 * link público. Os tipos espelham `server/lib/feedback/painel.ts` e `server/routes/*feedback.ts`; quem decide o que
 * cada pessoa vê é o SERVIDOR — a tela só mostra o que recebeu.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError, hasToken } from '../api';
import type { ChaveDoQuadrante, Nivel } from './quadrante';
import type { EstadoDaPauta } from './textos';

const BASE = '/api/v1/feedback';
const PUBLICO = '/api/v1/public/feedback';
const SEM_LOGIN = { skipAuth: true } as const;

// ── Tipos ──────────────────────────────────────────────────────────────────
export interface Analise { acordo: string[]; divergencia: string[]; destaque: string }
export interface AcaoDoPlano { acao: string; objetivo: string; responsavel: string; prazo: string; como_avaliar: string; status: string }
export interface PlanoPublicado { acoes: AcaoDoPlano[]; compromissos: { compromissos_colaborador: string; compromissos_lideranca: string } }
export interface PermissoesFeedback { papel: 'diretoria' | 'avaliador'; gerenciar: boolean; excluir: boolean; pdfCompleto: boolean }
export interface LinkPendente { linkId: string; caminho: string; criadoEmIso: string; expiraEmIso: string; dataValidade: string }

export interface FeedbackDaRodadaNaFicha {
  rodada: { id: string; nome: string };
  permissoes: PermissoesFeedback;
  participanteId: string;
  avaliador: { id: string; nome: string | null };
  autoavaliacao:
    | { situacao: 'concluida'; enviadaEmIso: string; respostas: Record<string, unknown>; origem: string }
    | { situacao: 'pendente'; link: LinkPendente | null };
  avaliacao:
    | { situacao: 'concluida'; respostas: Record<string, unknown>; atualizadaEmIso: string; avaliador: string | null; grupo: ChaveDoQuadrante }
    | { situacao: 'pendente' };
  comparacoes: Array<{ id: string; analise: Analise; geradaEmIso: string; geradaPor: string | null; modelo: string }>;
  pauta: { estado: EstadoDaPauta; campos: Record<string, string>; acoes: AcaoDoPlano[]; publicado: PlanoPublicado | null; linkDoPlano: LinkPendente | null } | null;
}
export interface CampoDaPauta { id: string; rotulo: string; tipo: string }
/** `campos: null` = a seção 14, onde a tabela do plano de ação entra. */
export interface SecaoDaPauta { id: string; titulo: string | null; campos: CampoDaPauta[] | null }
export interface ColunaDoPlano { id: keyof AcaoDoPlano; rotulo: string; tipo: string; opcoes?: Array<{ valor: string; rotulo: string }> }
/** O conteúdo literal que a aba desenha (`formulariosDaFicha`, `server/lib/feedback/painel.ts`). */
export interface FormulariosDoFeedback {
  versao: string;
  autoavaliacao: SecaoDoFormulario[];
  avaliacao: SecaoDoFormulario[];
  envioDaAvaliacao: { botao: string; enviada: string };
  pauta: SecaoDaPauta[];
  plano: { titulo: string; dica_celular: string; botao_adicionar: string; botao_remover_linha: string; colunas: ColunaDoPlano[] };
  textosDaPauta: { aviso: string; salvar: string; salvo: string; confirmaExcluir: string; rotuloEstado: string };
  caixasDaComparacao: Array<{ chave: keyof Analise; titulo: string; tipo: 'lista' | 'paragrafo' }>;
}
export interface FeedbackDaFicha { primeiroNome: string; nomeCompleto: string; rodadas: FeedbackDaRodadaNaFicha[]; formularios: FormulariosDoFeedback }

export interface LinhaDoPainel {
  participanteId: string;
  clienteId: string;
  nome: string;
  primeiroNome: string;
  avaliador: { id: string; nome: string | null };
  autoavaliacao: 'pendente' | 'concluida';
  avaliacao: 'pendente' | 'concluida';
  pauta: 'pendente' | EstadoDaPauta;
  desempenho: Nivel | null;
  valores: Nivel | null;
  impactoSaida: Nivel | null;
  grupo: ChaveDoQuadrante | null;
}
export interface PainelDaRodada {
  rodada: { id: string; nome: string };
  podeGerenciar: boolean;
  linhas: LinhaDoPainel[];
  cartoes: Array<{ chave: ChaveDoQuadrante; rotulo: string; quantos: number }>;
  grade: Array<{ desempenho: Nivel; valores: Nivel; grupo: ChaveDoQuadrante; quantos: number }>;
}
export interface MeuDesenvolvimentoItem { rodadaId: string; rodada: string; publicadoEmIso: string; plano: PlanoPublicado }
/** Os rótulos literais do plano publicado (`rotulosDoPlano`, servidor). */
export interface RotulosDoPlano { colunas: ColunaDoPlano[]; compromissos: Record<string, string> }

/** O formulário literal do antigo (as seções e perguntas vêm do servidor, da versão do conteúdo). */
export interface PerguntaDoFormulario {
  id: string;
  tipo: string;
  texto?: string;
  obrigatoria?: boolean | string;
  placeholder?: string;
  maxlength?: number;
  opcoes?: Array<{ valor: string; rotulo: string; descricao?: string | null } | string>;
  marcador_opcional?: string;
  ajuda?: string;
  ajuda_etapas?: string;
  condicional_a?: string;
  grupos?: Array<{ departamento: string | null; etapas: string[] }>;
  escala?: unknown;
}
export interface SecaoDoFormulario { id: string; tag?: string; rotulo?: string; titulo?: string | null; perguntas: PerguntaDoFormulario[] }

export type EstadoPublicoFeedback =
  | { estado: 'indisponivel' | 'muitas_tentativas' | 'vencido' | 'encerrado' }
  | {
      estado: 'respondendo';
      finalidade: 'autoavaliacao';
      nomeCompleto: string;
      primeiroNome: string;
      rodada: string;
      respostas: Record<string, unknown>;
      hero: { eyebrow: string; titulo: string; texto: string; meta: string[]; botao: string };
      envio: { texto_antes_do_botao: string; botao: string; botao_enviando: string; erro: string; tela_obrigado: { titulo: string; texto: string }; rodape: string };
      secoes: SecaoDoFormulario[];
    }
  | { estado: 'enviada'; finalidade: 'autoavaliacao'; primeiroNome: string; envio: { tela_obrigado: { titulo: string; texto: string } } }
  | { estado: 'plano'; finalidade: 'plano'; primeiroNome: string; rodada: string; publicado: PlanoPublicado; rotulos: RotulosDoPlano };

// ── Leitura ────────────────────────────────────────────────────────────────
async function ou404<T>(p: Promise<T>): Promise<T | null> {
  try {
    return await p;
  } catch (e) {
    // 404: esta pessoa não vê. 503: a flag está desligada (o `requireFlag` da casa). Nos dois, a parte não aparece.
    if (e instanceof ApiError && (e.status === 404 || e.status === 503)) return null;
    throw e;
  }
}

const chaveFicha = (clienteId: string) => ['v1', 'feedback', 'ficha', clienteId] as const;
const CHAVE_RODADAS = ['v1', 'feedback', 'rodadas'] as const;
const chavePainel = (rodadaId: string) => ['v1', 'feedback', 'painel', rodadaId] as const;

export function useFeedbackDaFicha(clienteId: string, ligado: boolean) {
  return useQuery({
    queryKey: chaveFicha(clienteId),
    queryFn: () => ou404(api.get<FeedbackDaFicha>(`${BASE}/fichas/${encodeURIComponent(clienteId)}`)),
    enabled: hasToken() && ligado && !!clienteId,
  });
}

export function useEuNoFeedback(ligado: boolean) {
  return useQuery({
    queryKey: ['v1', 'feedback', 'eu'] as const,
    queryFn: async () => (await ou404(api.get<{ podeAbrir: boolean; diretoria: boolean }>(`${BASE}/eu`))) ?? { podeAbrir: false, diretoria: false },
    enabled: hasToken() && ligado,
    staleTime: 60_000,
  });
}

export function useRodadas(ligado: boolean) {
  return useQuery({
    queryKey: CHAVE_RODADAS,
    queryFn: async () => (await ou404(api.get<{ rodadas: Array<{ id: string; nome: string }> }>(`${BASE}/rodadas`)))?.rodadas ?? null,
    enabled: hasToken() && ligado,
  });
}

export function usePainelDaRodada(rodadaId: string | null, ligado: boolean) {
  return useQuery({
    queryKey: chavePainel(rodadaId ?? ''),
    queryFn: () => ou404(api.get<PainelDaRodada>(`${BASE}/rodadas/${encodeURIComponent(rodadaId!)}`)),
    enabled: hasToken() && ligado && !!rodadaId,
  });
}

export function useCandidatos(rodadaId: string | null, ligado: boolean) {
  return useQuery({
    queryKey: ['v1', 'feedback', 'candidatos', rodadaId ?? ''] as const,
    queryFn: () =>
      api.get<{
        funcionarios: Array<{ clienteId: string; nome: string }>;
        avaliadores: Array<{ id: string; nome: string }>;
        /** Por pessoa: os avaliadores que JÁ ABREM a ficha dela no RH (o servidor decide; a tela só filtra). */
        podeAvaliar: Record<string, string[]>;
      }>(
        `${BASE}/rodadas/${encodeURIComponent(rodadaId!)}/candidatos`,
      ),
    enabled: hasToken() && ligado && !!rodadaId,
  });
}

export function useMeuDesenvolvimento(ligado: boolean) {
  return useQuery({
    queryKey: ['v1', 'feedback', 'meu-desenvolvimento'] as const,
    queryFn: () => ou404(api.get<{ itens: MeuDesenvolvimentoItem[]; rotulos: RotulosDoPlano }>(`${BASE}/meu-desenvolvimento`)),
    enabled: hasToken() && ligado,
  });
}

// ── Escrita ────────────────────────────────────────────────────────────────
export function useFeedbackActions(clienteId: string, rodadaId: string) {
  const qc = useQueryClient();
  const base = `${BASE}/fichas/${encodeURIComponent(clienteId)}/rodadas/${encodeURIComponent(rodadaId)}`;
  const recarregar = () => {
    void qc.invalidateQueries({ queryKey: chaveFicha(clienteId) });
    void qc.invalidateQueries({ queryKey: ['v1', 'feedback', 'painel'] });
  };
  const enviarLink = useMutation({ mutationFn: () => api.post<LinkPendente>(`${base}/autoavaliacao/link`, {}), onSuccess: recarregar });
  const enviarPlano = useMutation({ mutationFn: () => api.post<LinkPendente>(`${base}/plano/link`, {}), onSuccess: recarregar });
  const cancelarLink = useMutation({ mutationFn: (linkId: string) => api.post(`${BASE}/links/${encodeURIComponent(linkId)}/cancelar`, {}), onSuccess: recarregar });
  const salvarAvaliacao = useMutation({ mutationFn: (respostas: Record<string, unknown>) => api.put(`${base}/avaliacao`, { respostas }), onSuccess: recarregar });
  const gerarComparacao = useMutation({ mutationFn: () => api.post(`${base}/comparacao`, {}), onSuccess: recarregar });
  const salvarPauta = useMutation({ mutationFn: (v: { campos?: Record<string, string>; acoes?: AcaoDoPlano[] }) => api.put(`${base}/pauta`, v), onSuccess: recarregar });
  const mudarEstado = useMutation({ mutationFn: (estado: 'rascunho' | 'em_andamento' | 'concluida') => api.post(`${base}/pauta/estado`, { estado }), onSuccess: recarregar });
  const reabrir = useMutation({ mutationFn: () => api.post(`${base}/pauta/reabrir`, {}), onSuccess: recarregar });
  const excluir = useMutation({ mutationFn: (parte: 'autoavaliacao' | 'avaliacao' | 'pauta') => api.delete(`${base}/${parte}`), onSuccess: recarregar });
  return { enviarLink, enviarPlano, cancelarLink, salvarAvaliacao, gerarComparacao, salvarPauta, mudarEstado, reabrir, excluir };
}

export function usePainelActions(rodadaId: string | null) {
  const qc = useQueryClient();
  const recarregar = () => {
    void qc.invalidateQueries({ queryKey: ['v1', 'feedback'] });
  };
  return {
    criarRodada: useMutation({ mutationFn: (nome: string) => api.post<{ id: string; nome: string }>(`${BASE}/rodadas`, { nome }), onSuccess: recarregar }),
    incluir: useMutation({
      mutationFn: (itens: Array<{ clienteId: string; avaliadorId: string }>) => api.post(`${BASE}/rodadas/${encodeURIComponent(rodadaId!)}/participantes`, { itens }),
      onSuccess: recarregar,
    }),
    trocarAvaliador: useMutation({
      mutationFn: (v: { participanteId: string; avaliadorId: string }) =>
        api.patch<{ ok: true; antigo: { id: string; nome: string }; novo: { id: string; nome: string } }>(`${BASE}/participantes/${encodeURIComponent(v.participanteId)}`, { avaliadorId: v.avaliadorId }),
      onSuccess: recarregar,
    }),
  };
}

export const pdfDoFeedback = (clienteId: string, rodadaId: string, parte: 'autoavaliacao' | 'avaliacao' | 'comparacao' | 'pauta' | 'completo') =>
  api.download(`${BASE}/fichas/${encodeURIComponent(clienteId)}/rodadas/${encodeURIComponent(rodadaId)}/pdf/${parte}`);
export const pdfDoMeuPlano = (rodadaId: string) => api.download(`${BASE}/meu-desenvolvimento/${encodeURIComponent(rodadaId)}/pdf`);

// ── O link público ─────────────────────────────────────────────────────────
async function publico<T>(metodo: 'get' | 'post' | 'put', caminho: string, corpo?: unknown): Promise<T> {
  try {
    if (metodo === 'get') return await api.get<T>(caminho, SEM_LOGIN);
    if (metodo === 'put') return await api.put<T>(caminho, corpo ?? {}, SEM_LOGIN);
    return await api.post<T>(caminho, corpo ?? {}, SEM_LOGIN);
  } catch (e) {
    if (e instanceof ApiError) {
      if (e.status === 429) return { estado: 'muitas_tentativas' } as T;
      const estado = (e.response as { estado?: string } | undefined)?.estado;
      if (estado === 'vencido' || estado === 'encerrado') return { estado } as T;
    }
    throw e;
  }
}

export const feedbackPublico = {
  estado: (token: string) => publico<EstadoPublicoFeedback>('get', `${PUBLICO}/${encodeURIComponent(token)}`),
  responder: (token: string, chave: string, valor: string | string[] | null) =>
    publico<{ ok: true } | EstadoPublicoFeedback>('put', `${PUBLICO}/${encodeURIComponent(token)}/respostas/${encodeURIComponent(chave)}`, { valor }),
  enviar: (token: string) => publico<EstadoPublicoFeedback>('post', `${PUBLICO}/${encodeURIComponent(token)}/enviar`),
  caminhoDoPdf: (token: string) => `${PUBLICO}/${encodeURIComponent(token)}/pdf`,
};
